import React, { useEffect, useState } from 'react';

import { X } from 'lucide-react';

import { useInvoicePaymentSummary } from '../../hooks/useInvoiceApi';
import { useMemberApi } from '../../hooks/useMemberApi';
import PaymentSummary from '../payment/PaymentSummary';

// The wall at the end of the free tier. A free or unpaid member may mark 30 courses played (the backend's
// MembershipConstants.FreeCoursesPlayedLimit); the next tick comes back 403 and opens this.
//
// Deliberately a drawer on the map rather than a redirect to /payment-required. That page is for a
// member who is locked out of the whole app and whose only two choices are pay or log out. A free
// member is not locked out of anything: the map, their bucket list and their existing courses
// all keep working, and they are one tap away from carrying on. Throwing them onto a full-page
// paywall would say something untrue about where they stand.
//
// The invoice itself is not built here. ResumeMembership gets (or creates) the member's outstanding
// invoice and hands back its id, and PaymentSummary/useInvoicePaymentSummary show and pay it, the
// same pair the emailed "Pay Now" link and PaymentRequired both use. One payment UI, three doors.
interface UpgradeDrawerProps {
  isOpen: boolean;
  // What the backend refused with, shown as-is. It already names the limit, so this component does
  // not repeat the number and cannot drift from it.
  message: string | null;
  onClose: () => void;
}

const UpgradeDrawer: React.FC<UpgradeDrawerProps> = ({
  isOpen,
  message,
  onClose,
}) => {
  const { resumeMembership } = useMemberApi();
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The invoice is only asked for once the drawer is actually opened: creating one for every
  // visitor who merely loaded the map would be a real, tax-relevant document raised for nothing.
  useEffect(() => {
    if (!isOpen || invoiceId != null) {
      return;
    }

    let cancelled = false;

    resumeMembership()
      .then((response) => {
        if (cancelled) {
          return;
        }
        if (response?.success && response.data) {
          setInvoiceId(response.data);
        } else {
          setError(
            response?.message ?? 'Could not start your upgrade. Try again.',
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not start your upgrade. Try again.');
        }
      });

    return (): void => {
      cancelled = true;
    };
    // resumeMembership is rebuilt every render by useMemberApi, so listing it here would re-fire
    // this on each one and raise a fresh invoice request per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, invoiceId]);

  const { data: summary, isError: isSummaryError } = useInvoicePaymentSummary(
    invoiceId ?? undefined,
  );

  return (
    // Same shape and z-index as MemberSetupDrawer: rendered always, translated off-screen while
    // closed so it animates both ways, and unreachable by keyboard or screen reader while closed.
    <aside
      aria-hidden={!isOpen}
      aria-label="Upgrade your membership"
      className={`fixed inset-y-0 right-0 z-[60] flex w-full flex-col border-l border-primary/20 bg-charcoal shadow-2xl transition-transform duration-300 ease-in-out sm:w-96 ${
        isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
        <p className="text-sm font-bold uppercase tracking-wider text-white">
          Upgrade your membership
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-lg p-1 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-8 pt-5">
        <p className="text-base font-semibold text-white">
          Your free courses are all used up
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-white/50">
          {message ?? 'Upgrade your membership to keep marking courses played.'}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-white/50">
          Everything you have saved stays exactly as it is. Your bucket list and
          the map are unaffected.
        </p>

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300"
          >
            {error}
          </p>
        )}

        {isSummaryError && (
          <p
            role="alert"
            className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300"
          >
            We could not load your invoice. Please contact support.
          </p>
        )}

        <div className="mt-5 space-y-4">
          {summary && <PaymentSummary summary={summary} dark />}
          <button
            type="button"
            onClick={onClose}
            className="h-11 w-full rounded-lg border border-white/15 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10"
          >
            Not right now
          </button>
        </div>
      </div>
    </aside>
  );
};

export default UpgradeDrawer;
